// components/ROICalculator.tsx
// Interactive revenue impact calculator for the pillar page.
// User controls ALL inputs including projected conversion rate — no unverified claims.

import React, { useState, useMemo } from 'react';
import { Calculator, TrendingUp, ArrowRight } from 'lucide-react';

interface Inputs {
  avgTransaction: number;
  monthlySessions: number;
  currentConversion: number;
  projectedConversion: number;
}

const fmt = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

export const ROICalculator: React.FC = () => {
  const [inputs, setInputs] = useState<Inputs>({
    avgTransaction: 50,
    monthlySessions: 1000,
    currentConversion: 2.5,
    projectedConversion: 10,
  });

  const set = (k: keyof Inputs, v: number) => setInputs(p => ({ ...p, [k]: v }));

  const results = useMemo(() => {
    const { avgTransaction, monthlySessions, currentConversion, projectedConversion } = inputs;
    const cur = monthlySessions * (currentConversion / 100) * avgTransaction;
    const proj = monthlySessions * (projectedConversion / 100) * avgTransaction;
    const lift = proj - cur;
    const mult = currentConversion > 0 ? projectedConversion / currentConversion : 0;
    return { cur, proj, lift, annual: lift * 12, mult };
  }, [inputs]);

  return (
    <div className="bg-brand-surface rounded-2xl border border-brand-border p-8 my-12">
      {/* Header */}
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 bg-brand-black rounded-xl flex items-center justify-center">
          <Calculator className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-brand-black">Revenue Impact Calculator</h3>
          <p className="text-xs text-brand-gray font-mono uppercase tracking-wider">
            Model your own numbers
          </p>
        </div>
      </div>

      <p className="text-sm text-brand-charcoal mb-8">
        Adjust every input below — including the projected conversion rate. We don't assume a
        specific lift. You decide what's realistic for your vertical and merchant network.
      </p>

      {/* Inputs */}
      <div className="grid sm:grid-cols-2 gap-6 mb-8">
        <Field label="Avg Transaction Value" prefix="$">
          <input
            type="number" min={1} value={inputs.avgTransaction}
            onChange={e => set('avgTransaction', +e.target.value)}
            className="w-full rounded-xl border border-brand-border px-4 py-3 text-brand-black font-medium focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
          />
        </Field>

        <Field label="Monthly Search Sessions">
          <input
            type="number" min={1} value={inputs.monthlySessions}
            onChange={e => set('monthlySessions', +e.target.value)}
            className="w-full rounded-xl border border-brand-border px-4 py-3 text-brand-black font-medium focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
          />
        </Field>

        <Field label="Current Conversion Rate" suffix="%">
          <input
            type="number" min={0} max={100} step={0.1} value={inputs.currentConversion}
            onChange={e => set('currentConversion', +e.target.value)}
            className="w-full rounded-xl border border-brand-border px-4 py-3 text-brand-black font-medium focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
          />
        </Field>

        <Field label="Projected Conversion Rate" suffix="%" hint="You set this — we don't assume a specific lift.">
          <input
            type="number" min={0} max={100} step={0.1} value={inputs.projectedConversion}
            onChange={e => set('projectedConversion', +e.target.value)}
            className="w-full rounded-xl border border-brand-border px-4 py-3 text-brand-black font-medium focus:outline-none focus:border-brand-accent focus:ring-1 focus:ring-brand-accent"
          />
        </Field>
      </div>

      {/* Results */}
      <div className="bg-brand-white rounded-xl border border-brand-border p-6">
        <div className="grid sm:grid-cols-3 gap-6 mb-6">
          <Stat label="Current Monthly" value={fmt(results.cur)} />
          <div className="flex flex-col items-center justify-center">
            <ArrowRight className="w-5 h-5 text-brand-accent mb-1" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-brand-gray">
              {results.mult.toFixed(1)}x
            </span>
          </div>
          <Stat label="Projected Monthly" value={fmt(results.proj)} accent />
        </div>

        <div className="flex items-center justify-center gap-3 pt-4 border-t border-brand-border">
          <TrendingUp className="w-5 h-5 text-green-600" />
          <div className="text-center">
            <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray">
              Projected Annual Uplift
            </p>
            <p className="text-3xl font-bold text-green-600">{fmt(results.annual)}</p>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-brand-gray text-center mt-4">
        This is a projection model, not a guarantee. Actual results depend on vertical,
        merchant network depth, and query complexity.
      </p>
    </div>
  );
};


// ---- tiny sub-components to keep the JSX clean ----

const Field: React.FC<{
  label: string;
  prefix?: string;
  suffix?: string;
  hint?: string;
  children: React.ReactNode;
}> = ({ label, prefix, suffix, hint, children }) => (
  <div>
    <label className="block text-xs font-mono uppercase tracking-wider text-brand-gray mb-2">
      {label}
    </label>
    <div className="flex items-center gap-2">
      {prefix && <span className="text-brand-gray text-sm">{prefix}</span>}
      {children}
      {suffix && <span className="text-brand-gray text-sm">{suffix}</span>}
    </div>
    {hint && <p className="text-[10px] text-brand-gray mt-1">{hint}</p>}
  </div>
);

const Stat: React.FC<{ label: string; value: string; accent?: boolean }> = ({ label, value, accent }) => (
  <div className="text-center">
    <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gray mb-1">{label}</p>
    <p className={`text-2xl font-bold ${accent ? 'text-brand-accent' : 'text-brand-charcoal'}`}>{value}</p>
  </div>
);